import type { FailureMode, Method, Rule } from './madrobot.models';
import { CLIENT_TEMP_ID_PREFIX } from './madrobot.schema';

export const METHOD_OPTIONS: { key: Method; text: string; value: Method }[] = [
  { key: '*', text: 'Any', value: '*' },
  { key: 'GET', text: 'GET', value: 'GET' },
  { key: 'POST', text: 'POST', value: 'POST' },
  { key: 'PUT', text: 'PUT', value: 'PUT' },
  { key: 'PATCH', text: 'PATCH', value: 'PATCH' },
  { key: 'DELETE', text: 'DELETE', value: 'DELETE' },
];

export const MODE_OPTIONS: {
  key: FailureMode['kind'];
  text: string;
  value: FailureMode['kind'];
}[] = [
  { key: 'httpError', text: 'HTTP error status', value: 'httpError' },
  { key: 'latency', text: 'Latency (delay)', value: 'latency' },
  { key: 'networkDrop', text: 'Network drop', value: 'networkDrop' },
  { key: 'invalidJson', text: 'Invalid JSON', value: 'invalidJson' },
  { key: 'wrongShape', text: 'Static JSON response', value: 'wrongShape' },
];

export const defaultModeFor = (kind: FailureMode['kind']): FailureMode => {
  switch (kind) {
    case 'httpError':
      return { kind, status: 500 };
    case 'latency':
      return { kind, delayMs: 3000 };
    case 'networkDrop':
      return { kind };
    case 'invalidJson':
      return { kind };
    case 'wrongShape':
      return { kind, payload: {} };
    default:
      throw new Error('Unknown Mad Robot failure mode');
  }
};

export const summarizeMode = (mode: FailureMode): string => {
  switch (mode.kind) {
    case 'httpError':
      return `HTTP ${mode.status}`;
    case 'latency':
      return `Delay ${mode.delayMs}ms`;
    case 'networkDrop':
      return 'Drop socket';
    case 'invalidJson':
      return 'Invalid JSON';
    case 'wrongShape':
      return mode.status && mode.status !== 200
        ? `Static JSON ${mode.status}`
        : 'Static JSON';
    default:
      return '';
  }
};

/**
 * Stable React key for a not-yet-saved rule (the server assigns the real id on
 * save). Uses Math.random rather than `crypto.randomUUID`, which needs a secure
 * context that on-prem HTTP deployments may not have.
 */
export const tempId = () =>
  `${CLIENT_TEMP_ID_PREFIX}${Math.random().toString(36).slice(2, 12)}`;

/**
 * JSON.stringify with recursively sorted object keys. Zod can re-order keys on
 * either side of the wire, so comparing rules by raw JSON gives spurious diffs;
 * sorting keys makes structurally equal rules serialize identically. Array
 * order is preserved — reordering rules is a real change.
 */
export const stableStringify = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const body = Object.keys(record)
      .sort()
      .map(key => `${JSON.stringify(key)}:${stableStringify(record[key])}`)
      .join(',');
    return `{${body}}`;
  }
  return JSON.stringify(value);
};

export const blankDraft = (): Rule => ({
  id: tempId(),
  name: 'New rule',
  pathPattern: '/applications{/*rest}',
  method: '*',
  mode: defaultModeFor('httpError'),
  probability: 1,
  enabled: true,
});

export const downloadRulesFile = (rules: Rule[]) => {
  const payload = { version: 1 as const, rules };
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `madrobot-rules-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
